import { useTranslation } from 'react-i18next';

import PageTitle from '../../shared/PageTitle/PageTitle';
import { toUiLang, UiLang } from '../../seo/site';

import styles from './styles.module.scss';

type PolicyCopy = {
  updated: string;
  intro: string;
  sections: Array<{ title: string; paragraphs: string[] }>;
  storageTitle: string;
  storageIntro: string;
  storageHeaders: [string, string, string];
  storageRows: Array<[string, string, string]>;
  googleText: string;
  choicesText: string;
  authorityText: string;
  authorityLabel: string;
};

const POLICY: Record<UiLang, PolicyCopy> = {
  en: {
    updated: 'Last updated: 11 October 2026',
    intro: 'This notice explains how Fresh Grass handles personal data when you use this website, contact us or place an order.',
    sections: [
      {
        title: '1. Who is responsible',
        paragraphs: [
          'Fresh Grass, Begijnenmeers 8, 1770 Liedekerke, Belgium, is responsible for the processing described here. Contact us at info@freshgrass.pet for privacy questions.',
        ],
      },
      {
        title: '2. Data we use and why',
        paragraphs: [
          'We use contact, delivery, order and payment-status information to process purchases, deliver products, provide support, prevent fraud and meet accounting or other legal duties. Payment-card details are handled by Stripe and are not stored by this website.',
          'With your consent, we use analytics information to understand website use and advertising information to measure and improve campaigns. We do not use optional analytics or advertising storage unless you allow the relevant category.',
        ],
      },
      {
        title: '3. Legal bases',
        paragraphs: [
          'We process data when needed to perform a contract or take requested steps before a purchase, comply with legal obligations, pursue legitimate interests such as security and customer support, or when you have given consent. You may withdraw consent at any time.',
        ],
      },
      {
        title: '4. Recipients and international transfers',
        paragraphs: [
          'Data is shared only as needed with service providers such as hosting, delivery, email and payment providers. Stripe processes payments. If you consent, Google services configured through Google Tag Manager may process analytics or advertising data.',
          'Some providers may process data outside the European Economic Area. Where required, transfers rely on the safeguards offered by the relevant provider, such as an adequacy decision or standard contractual clauses.',
        ],
      },
      {
        title: '5. Retention',
        paragraphs: [
          'We keep order and transaction records for the period required by applicable accounting, tax and consumer law. Support records are kept only as long as reasonably needed. Optional measurement data follows the retention configured in the relevant provider. Browser-storage periods are listed below.',
        ],
      },
      {
        title: '6. Your rights',
        paragraphs: [
          'Subject to applicable law, you may request access, correction, deletion, restriction, portability or object to processing. You may also withdraw consent without affecting processing that occurred before withdrawal. Contact info@freshgrass.pet to exercise a right.',
        ],
      },
    ],
    storageTitle: '7. Cookies, browser storage and Google Consent Mode',
    storageIntro: 'The site uses the following first-party browser storage. Third-party cookies may be added by Google only for categories you permit and depend on the tags configured in Google Tag Manager.',
    storageHeaders: ['Name/category', 'Purpose', 'Duration'],
    storageRows: [
      ['fg-google-consent', 'Stores the consent version and your analytics/advertising choices.', '180 days'],
      ['fs-lng / language cookie', 'Remembers the language you selected.', 'Until removed or replaced'],
      ['fg-cartStorage', 'Keeps products in your shopping cart.', 'Until checkout, clearing the cart or browser storage'],
      ['fg-tracked-transactions', 'Prevents the same confirmed purchase from being counted twice in analytics.', 'Until browser storage is cleared; maximum 50 transaction identifiers'],
      ['Google analytics/advertising storage', 'Measures site use or advertising only for the category you accept. Names and duration depend on the GTM tags in use.', 'As configured in Google services'],
    ],
    googleText: 'Google Tag Manager loads using Advanced Consent Mode. Before a choice, all optional consent signals are denied. Google tags may send limited cookieless pings while denied; optional cookies or identifiers are available only after consent for that category.',
    choicesText: 'Use “Cookie settings” in the footer at any time to accept, refuse or change analytics and advertising choices. Refusing optional categories does not prevent shopping or using the website.',
    authorityText: 'If you believe that your personal data has been processed unlawfully, you have the right to lodge a complaint with the',
    authorityLabel: 'Belgian Data Protection Authority',
  },
  nl: {
    updated: 'Laatst bijgewerkt: 11 oktober 2026',
    intro: 'Deze verklaring legt uit hoe Fresh Grass persoonsgegevens verwerkt wanneer u deze website gebruikt, contact met ons opneemt of een bestelling plaatst.',
    sections: [
      {
        title: '1. Wie is verantwoordelijk',
        paragraphs: [
          'Fresh Grass, Begijnenmeers 8, 1770 Liedekerke, België, is verantwoordelijk voor de hier beschreven verwerking. Neem voor privacyvragen contact op via info@freshgrass.pet.',
        ],
      },
      {
        title: '2. Welke gegevens we gebruiken en waarom',
        paragraphs: [
          'We gebruiken contact-, bezorg-, bestel- en betalingsstatusgegevens om aankopen uit te voeren, producten te bezorgen, ondersteuning te geven, fraude te voorkomen en aan boekhoudkundige of andere wettelijke verplichtingen te voldoen. Betaalkaartgegevens worden door Stripe verwerkt en niet door deze website opgeslagen.',
          'Met uw toestemming gebruiken we analysegegevens om websitegebruik te begrijpen en advertentiegegevens om campagnes te meten en te verbeteren. We gebruiken geen optionele analyse- of advertentieopslag tenzij u de betreffende categorie toestaat.',
        ],
      },
      {
        title: '3. Rechtsgronden',
        paragraphs: [
          'We verwerken gegevens wanneer dit nodig is voor een overeenkomst of gevraagde stappen vóór een aankoop, voor wettelijke verplichtingen, voor gerechtvaardigde belangen zoals beveiliging en klantenservice, of met uw toestemming. U kunt toestemming altijd intrekken.',
        ],
      },
      {
        title: '4. Ontvangers en internationale doorgiften',
        paragraphs: [
          'Gegevens worden alleen waar nodig gedeeld met dienstverleners voor bijvoorbeeld hosting, bezorging, e-mail en betalingen. Stripe verwerkt betalingen. Als u toestemming geeft, kunnen Google-diensten die via Google Tag Manager zijn ingesteld analyse- of advertentiegegevens verwerken.',
          'Sommige leveranciers kunnen gegevens buiten de Europese Economische Ruimte verwerken. Waar nodig steunen doorgiften op passende waarborgen van de leverancier, zoals een adequaatheidsbesluit of standaardcontractbepalingen.',
        ],
      },
      {
        title: '5. Bewaartermijnen',
        paragraphs: [
          'Bestel- en transactiegegevens worden bewaard zolang de toepasselijke boekhoud-, belasting- en consumentenwetgeving dit vereist. Supportgegevens bewaren we alleen zolang dat redelijkerwijs nodig is. Voor optionele meetgegevens geldt de bewaartermijn die bij de betreffende leverancier is ingesteld. Browseropslag staat hieronder.',
        ],
      },
      {
        title: '6. Uw rechten',
        paragraphs: [
          'Volgens de toepasselijke wet kunt u inzage, correctie, verwijdering, beperking of overdraagbaarheid vragen, of bezwaar maken. U kunt toestemming intrekken zonder dat dit eerdere verwerking ongeldig maakt. Neem contact op via info@freshgrass.pet.',
        ],
      },
    ],
    storageTitle: '7. Cookies, browseropslag en Google Consent Mode',
    storageIntro: 'De site gebruikt onderstaande eigen browseropslag. Google kan alleen cookies van derden toevoegen voor categorieën die u toestaat; dit hangt af van de tags in Google Tag Manager.',
    storageHeaders: ['Naam/categorie', 'Doel', 'Duur'],
    storageRows: [
      ['fg-google-consent', 'Bewaart de toestemmingsversie en uw analyse-/advertentiekeuzes.', '180 dagen'],
      ['fs-lng / taalcookie', 'Onthoudt de gekozen taal.', 'Tot verwijdering of vervanging'],
      ['fg-cartStorage', 'Bewaart producten in uw winkelmand.', 'Tot aankoop, legen van de winkelmand of browseropslag'],
      ['fg-tracked-transactions', 'Voorkomt dat dezelfde bevestigde aankoop dubbel in analytics wordt geteld.', 'Tot browseropslag wordt gewist; maximaal 50 transactie-ID’s'],
      ['Google analyse-/advertentieopslag', 'Meet gebruik of advertenties alleen voor de categorie die u accepteert. Namen en duur hangen af van de GTM-tags.', 'Zoals ingesteld in Google-diensten'],
    ],
    googleText: 'Google Tag Manager wordt geladen met Advanced Consent Mode. Vóór uw keuze staan alle optionele toestemmingssignalen op geweigerd. Google-tags kunnen dan beperkte cookieloze pings sturen; optionele cookies of identifiers zijn pas beschikbaar na toestemming voor die categorie.',
    choicesText: 'Gebruik op elk moment “Cookie-instellingen” in de footer om analyse en advertenties te accepteren, weigeren of wijzigen. Weigeren verhindert winkelen of websitegebruik niet.',
    authorityText: 'Als u denkt dat uw persoonsgegevens onrechtmatig zijn verwerkt, hebt u het recht een klacht in te dienen bij de',
    authorityLabel: 'Gegevensbeschermingsautoriteit',
  },
  fr: {
    updated: 'Dernière mise à jour : 11 octobre 2026',
    intro: 'La présente notice explique comment Fresh Grass traite les données personnelles lorsque vous utilisez ce site, nous contactez ou passez une commande.',
    sections: [
      {
        title: '1. Responsable du traitement',
        paragraphs: [
          'Fresh Grass, Begijnenmeers 8, 1770 Liedekerke, Belgique, est responsable des traitements décrits ici. Pour toute question, écrivez à info@freshgrass.pet.',
        ],
      },
      {
        title: '2. Données utilisées et finalités',
        paragraphs: [
          'Nous utilisons les coordonnées, informations de livraison, de commande et de statut du paiement pour traiter les achats, livrer, fournir une assistance, prévenir la fraude et respecter nos obligations comptables ou légales. Les données de carte sont traitées par Stripe et ne sont pas stockées par ce site.',
          'Avec votre consentement, nous utilisons des données de mesure pour comprendre l’utilisation du site et des données publicitaires pour mesurer et améliorer les campagnes. Aucun stockage facultatif d’analyse ou de publicité n’est utilisé sans votre accord pour la catégorie concernée.',
        ],
      },
      {
        title: '3. Bases juridiques',
        paragraphs: [
          'Nous traitons les données pour exécuter un contrat ou prendre les mesures demandées avant un achat, respecter une obligation légale, poursuivre des intérêts légitimes comme la sécurité et l’assistance, ou sur la base de votre consentement. Vous pouvez retirer votre consentement à tout moment.',
        ],
      },
      {
        title: '4. Destinataires et transferts internationaux',
        paragraphs: [
          'Les données sont partagées uniquement si nécessaire avec des prestataires d’hébergement, livraison, e-mail ou paiement. Stripe traite les paiements. Si vous y consentez, les services Google configurés via Google Tag Manager peuvent traiter des données d’analyse ou de publicité.',
          'Certains prestataires peuvent traiter des données hors de l’Espace économique européen. Lorsque nécessaire, les transferts reposent sur les garanties du prestataire, comme une décision d’adéquation ou des clauses contractuelles types.',
        ],
      },
      {
        title: '5. Conservation',
        paragraphs: [
          'Les documents de commande et de transaction sont conservés pendant la durée exigée par les règles comptables, fiscales et de consommation applicables. L’assistance n’est conservée que le temps raisonnablement nécessaire. Les données facultatives suivent la durée configurée chez le prestataire. Les durées du stockage navigateur figurent ci-dessous.',
        ],
      },
      {
        title: '6. Vos droits',
        paragraphs: [
          'Selon la loi applicable, vous pouvez demander l’accès, la rectification, l’effacement, la limitation ou la portabilité, ou vous opposer au traitement. Le retrait du consentement n’affecte pas les traitements antérieurs. Contactez info@freshgrass.pet.',
        ],
      },
    ],
    storageTitle: '7. Cookies, stockage navigateur et Google Consent Mode',
    storageIntro: 'Le site utilise les stockages internes ci-dessous. Google ne peut ajouter des cookies tiers que pour les catégories autorisées, selon les balises configurées dans Google Tag Manager.',
    storageHeaders: ['Nom/catégorie', 'Finalité', 'Durée'],
    storageRows: [
      ['fg-google-consent', 'Conserve la version du consentement et vos choix d’analyse/publicité.', '180 jours'],
      ['fs-lng / cookie de langue', 'Mémorise la langue sélectionnée.', 'Jusqu’à suppression ou remplacement'],
      ['fg-cartStorage', 'Conserve les produits du panier.', 'Jusqu’au paiement, à la suppression du panier ou du stockage'],
      ['fg-tracked-transactions', 'Évite de compter deux fois le même achat confirmé dans les statistiques.', 'Jusqu’à suppression du stockage ; 50 identifiants maximum'],
      ['Stockage Google analyse/publicité', 'Mesure le site ou la publicité uniquement pour la catégorie acceptée. Noms et durée selon les balises GTM.', 'Selon la configuration des services Google'],
    ],
    googleText: 'Google Tag Manager utilise le mode de consentement avancé. Avant votre choix, tous les signaux facultatifs sont refusés. Les balises Google peuvent envoyer des signaux limités sans cookie ; les cookies ou identifiants facultatifs ne sont disponibles qu’après accord pour la catégorie.',
    choicesText: 'Utilisez « Paramètres des cookies » dans le pied de page à tout moment pour accepter, refuser ou modifier vos choix. Le refus n’empêche pas les achats ni l’utilisation du site.',
    authorityText: 'Si vous estimez que vos données personnelles ont été traitées illégalement, vous avez le droit d’introduire une plainte auprès de',
    authorityLabel: 'l’Autorité de protection des données',
  },
  de: {
    updated: 'Zuletzt aktualisiert: 11. Oktober 2026',
    intro: 'Diese Erklärung beschreibt, wie Fresh Grass personenbezogene Daten verarbeitet, wenn Sie diese Website nutzen, uns kontaktieren oder bestellen.',
    sections: [
      {
        title: '1. Verantwortlicher',
        paragraphs: [
          'Fresh Grass, Begijnenmeers 8, 1770 Liedekerke, Belgien, ist für die hier beschriebene Verarbeitung verantwortlich. Datenschutzfragen senden Sie an info@freshgrass.pet.',
        ],
      },
      {
        title: '2. Verwendete Daten und Zwecke',
        paragraphs: [
          'Wir verwenden Kontakt-, Liefer-, Bestell- und Zahlungsstatusdaten, um Käufe abzuwickeln, Produkte zu liefern, Support zu leisten, Betrug zu verhindern und gesetzliche Pflichten zu erfüllen. Kartendaten verarbeitet Stripe; diese Website speichert sie nicht.',
          'Mit Ihrer Einwilligung verwenden wir Analysedaten, um die Websitenutzung zu verstehen, und Werbedaten, um Kampagnen zu messen und zu verbessern. Optionale Analyse- oder Werbespeicherung erfolgt nur mit Ihrer Einwilligung für die jeweilige Kategorie.',
        ],
      },
      {
        title: '3. Rechtsgrundlagen',
        paragraphs: [
          'Wir verarbeiten Daten zur Vertragserfüllung oder für angeforderte Schritte vor dem Kauf, zur Erfüllung gesetzlicher Pflichten, aufgrund berechtigter Interessen wie Sicherheit und Support oder mit Ihrer Einwilligung. Sie können eine Einwilligung jederzeit widerrufen.',
        ],
      },
      {
        title: '4. Empfänger und internationale Übermittlungen',
        paragraphs: [
          'Daten werden nur soweit nötig mit Dienstleistern für Hosting, Lieferung, E-Mail und Zahlungen geteilt. Stripe verarbeitet Zahlungen. Mit Ihrer Einwilligung können über Google Tag Manager konfigurierte Google-Dienste Analyse- oder Werbedaten verarbeiten.',
          'Einige Anbieter können Daten außerhalb des Europäischen Wirtschaftsraums verarbeiten. Soweit erforderlich, beruhen Übermittlungen auf Garantien des Anbieters, etwa Angemessenheitsbeschlüssen oder Standardvertragsklauseln.',
        ],
      },
      {
        title: '5. Speicherdauer',
        paragraphs: [
          'Bestell- und Transaktionsunterlagen werden so lange gespeichert, wie es Buchhaltungs-, Steuer- und Verbraucherrecht verlangt. Supportdaten nur solange wie erforderlich. Für optionale Messdaten gilt die beim Anbieter konfigurierte Dauer. Browser-Speicherfristen stehen unten.',
        ],
      },
      {
        title: '6. Ihre Rechte',
        paragraphs: [
          'Nach geltendem Recht können Sie Auskunft, Berichtigung, Löschung, Einschränkung oder Übertragbarkeit verlangen oder widersprechen. Ein Widerruf berührt frühere Verarbeitung nicht. Kontaktieren Sie info@freshgrass.pet.',
        ],
      },
    ],
    storageTitle: '7. Cookies, Browser-Speicher und Google Consent Mode',
    storageIntro: 'Die Website nutzt den folgenden eigenen Browser-Speicher. Google kann Drittanbieter-Cookies nur für erlaubte Kategorien hinzufügen; dies hängt von den in Google Tag Manager konfigurierten Tags ab.',
    storageHeaders: ['Name/Kategorie', 'Zweck', 'Dauer'],
    storageRows: [
      ['fg-google-consent', 'Speichert Einwilligungsversion sowie Analyse-/Werbeauswahl.', '180 Tage'],
      ['fs-lng / Sprach-Cookie', 'Merkt sich die gewählte Sprache.', 'Bis zur Löschung oder Ersetzung'],
      ['fg-cartStorage', 'Speichert Produkte im Warenkorb.', 'Bis Kauf, Warenkorbleerung oder Löschen des Browser-Speichers'],
      ['fg-tracked-transactions', 'Verhindert doppelte Analysezählung desselben bestätigten Kaufs.', 'Bis Browser-Speicher gelöscht wird; maximal 50 Transaktions-IDs'],
      ['Google Analyse-/Werbespeicher', 'Misst Nutzung oder Werbung nur für erlaubte Kategorien. Namen und Dauer richten sich nach GTM-Tags.', 'Wie in Google-Diensten konfiguriert'],
    ],
    googleText: 'Google Tag Manager wird im erweiterten Einwilligungsmodus geladen. Vor Ihrer Auswahl sind alle optionalen Signale abgelehnt. Google-Tags können begrenzte cookielose Pings senden; optionale Cookies oder Kennungen sind erst nach Einwilligung für die Kategorie verfügbar.',
    choicesText: 'Über „Cookie-Einstellungen“ in der Fußzeile können Sie Analyse und Werbung jederzeit akzeptieren, ablehnen oder ändern. Eine Ablehnung verhindert Einkäufe oder die Websitenutzung nicht.',
    authorityText: 'Wenn Sie der Ansicht sind, dass Ihre personenbezogenen Daten unrechtmäßig verarbeitet wurden, haben Sie das Recht, eine Beschwerde einzureichen bei der',
    authorityLabel: 'belgischen Datenschutzbehörde',
  },
};

const PrivacyPolicy = () => {
  const { t, i18n } = useTranslation();
  const language = toUiLang(i18n.resolvedLanguage || i18n.language);
  const copy = POLICY[language];

  return (
    <div className={styles.privacyPolicy}>
      <div className="container">
        <PageTitle align="center">{t('privacyPolicy')}</PageTitle>
        <article className={styles.privacyPolicy_content}>
          <p className={styles.updated}>{copy.updated}</p>
          <p>{copy.intro}</p>

          {copy.sections.map((section) => (
            <section key={section.title}>
              <h2>{section.title}</h2>
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </section>
          ))}

          <section>
            <h2>{copy.storageTitle}</h2>
            <p>{copy.storageIntro}</p>
            <div className={styles.tableWrapper}>
              <table>
                <thead>
                  <tr>
                    {copy.storageHeaders.map((header) => <th key={header}>{header}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {copy.storageRows.map((row) => (
                    <tr key={row[0]}>
                      {row.map((cell) => <td key={cell}>{cell}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p>{copy.googleText}</p>
            <p>{copy.choicesText}</p>
          </section>

          <section>
            <h2>8. {language === 'en' ? 'Complaints' : language === 'nl' ? 'Klachten' : language === 'fr' ? 'Réclamations' : 'Beschwerden'}</h2>
            <p>
              {copy.authorityText}{' '}
              <a href="https://www.dataprotectionauthority.be/" target="_blank" rel="noreferrer">
                {copy.authorityLabel}
              </a>
            </p>
          </section>
        </article>
      </div>
    </div>
  );
};

export default PrivacyPolicy;